import React, { useEffect } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { ConfirmProvider } from './components/ConfirmModal';
import FormWindowProvider from './components/FormWindow/FormWindowProvider';
import AppRoutes from './routes';
import ErrorBoundary from './components/ErrorBoundary';
import OfflineBanner from './components/OfflineBanner';
import { queryClient } from './query/queryClient';

/**
 * App root. Architecture Rule 36: ErrorBoundary at top + react-hot-toast Toaster.
 * Frontend quality baseline: TanStack Query owns server state and cache.
 *
 * Provider order:
 *   ErrorBoundary -> QueryClientProvider -> AuthProvider -> ConfirmProvider
 *   -> BrowserRouter -> FormWindowProvider -> AppRoutes
 *   <Toaster /> and <OfflineBanner /> stay outside the route tree.
 *
 * FormWindowProvider sits INSIDE the router but ABOVE the routes on purpose: a
 * half-typed Add/Edit form is work in progress, not a property of the page it
 * was opened from, so parking one and navigating elsewhere must not unmount it.
 * It needs `useNavigate` (View full page) and `useConfirm` (discard a dirty
 * draft), which is what pins it between those two providers.
 */
export default function App() {
  useEffect(() => {
    if (window.__hideLoader) window.__hideLoader();
  }, []);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <ConfirmProvider>
              <BrowserRouter>
                <FormWindowProvider>
                  <AppRoutes />
                </FormWindowProvider>
              </BrowserRouter>
            </ConfirmProvider>
          </AuthProvider>
        </ThemeProvider>
        {process.env.REACT_APP_QUERY_DEVTOOLS === 'true' && (
          <ReactQueryDevtools initialIsOpen={false} />
        )}
      </QueryClientProvider>
      <OfflineBanner />
      {/* Rule 56 — react-hot-toast ships a hardcoded white panel, which reads
          as a hole punched in the app under the dark theme. Handing it the
          surface/text/border tokens (and the semantic accents for the
          success/error icons) keeps toasts inside the design system in BOTH
          themes; `var()` resolves at paint time, so the theme toggle is
          picked up without remounting the Toaster. */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: 'var(--surface)',
            color: 'var(--text)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            boxShadow: 'var(--shadow-pop)',
            fontSize: '0.82rem',
            maxWidth: '26rem',
          },
          success: { duration: 3000, iconTheme: { primary: 'var(--success)', secondary: 'var(--surface)' } },
          error:   { duration: 6000, iconTheme: { primary: 'var(--danger)',  secondary: 'var(--surface)' } },
        }}
      />
    </ErrorBoundary>
  );
}
